import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';
import elevation from '@/theme/elevation';

import { Button } from '@/components/atoms/Button/Button';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const CallToActionWebinar = ({
  date,
  time,
  location = 'Online',
  headerImageUrl,
  onClick,
  isLoading,
}: CallToActionWebinarProps) => {
  return (
    <Stack
      onClick={() => onClick?.('url')}
      sx={{
        borderRadius: sp[8],
        width: '310px',
        minWidth: '310px',
        maxWidth: '310px',
        height: '445px',
        maxHeight: '445px',
        boxSizing: 'border-box',
        background: '#ffffff',
        boxShadow: elevation[1],
        overflow: 'hidden',
        userSelect: 'none',
        border: '1px solid #cbc5b9',
      }}
    >
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <Box
            sx={{
              minHeight: '180px',
              height: '180px',
              maxHeight: '180px',
              display: 'flex',
              '& > img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                userSelect: 'none',
                WebkitUserDrag: 'none',
              },
            }}
          >
            <Skeleton shape='header' isSquare />
          </Box>
        }
        content={
          <Box
            sx={{
              minHeight: '180px',
              height: '180px',
              maxHeight: '180px',
              display: 'flex',
              '& > img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                userSelect: 'none',
                WebkitUserDrag: 'none',
              },
            }}
          >
            <img
              src={headerImageUrl}
              alt='header'
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src =
                  'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg?h=180';
              }}
            />
          </Box>
        }
      />
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <Stack
            sx={{
              p: `${sp[24]} 36px`,
              alignItems: 'center',
              gap: sp[4],
              maxHeight: '150px',
            }}
          >
            <Skeleton shape='paragraph' />
            <Skeleton shape='button' />
          </Stack>
        }
        content={
          <Stack sx={{ p: `${sp[24]} 36px`, gap: sp[4], maxHeight: '150px' }}>
            <Box sx={{ typography: 'headingXs', color: '#404042' }}>
              Book This Webinar
            </Box>
            <Stack sx={{ mb: sp[4], p: sp[4] }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: sp[4],
                }}
              >
                <Box sx={{ typography: 'bodySmSemibold', color: '#404042' }}>
                  Day:
                </Box>
                <Box sx={{ typography: 'bodyMd', color: '#404042' }}>
                  {days[date?.getDay()]}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: sp[4],
                }}
              >
                <Box sx={{ typography: 'bodySmSemibold', color: '#404042' }}>
                  Date:
                </Box>
                <Box sx={{ typography: 'bodyMd', color: '#404042' }}>
                  {date?.toLocaleDateString()}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: sp[4],
                }}
              >
                <Box sx={{ typography: 'bodySmSemibold', color: '#404042' }}>
                  Time:
                </Box>
                <Box sx={{ typography: 'bodyMd', color: '#404042' }}>
                  {time}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: sp[4],
                }}
              >
                <Box sx={{ typography: 'bodySmSemibold', color: '#404042' }}>
                  Location:
                </Box>
                <Box sx={{ typography: 'bodyMd', color: '#404042' }}>
                  {location}
                </Box>
              </Box>
            </Stack>
            <Button ariaLabel='Book this Webinar'>Book this webinar</Button>
          </Stack>
        }
      />
    </Stack>
  );
};

interface CallToActionWebinarProps {
  date: Date;
  time: string;
  location?: string;
  headerImageUrl?: string;
  onClick?: (url: string) => void;
  isLoading?: boolean;
}

CallToActionWebinar.displayName = 'CallToActionWebinar';

export default CallToActionWebinar;
